<script setup lang="ts">
import type { CallinglyCall, CallUser } from '@/types/types'

defineProps<{
  status: string | null
  call: CallinglyCall | null
  callUser: CallUser | null
  isMuted: boolean
  isOnHold: boolean
  isTransferStarted: boolean
}>()

const emit = defineEmits<{
  accept: []
  reject: []
  hold: []
  unhold: []
  transfer: []
  dialpad: []
  mute: []
  unmute: []
  hangup: []
}>()

// Shared metrics for the round in-call buttons and their captions.
const ctrl = 'text-center cursor-pointer w-[70px] float-right mx-[10px] my-0'
const dialer = 'w-[60px] h-[60px] rounded-full border-2 border-[#073b4c] m-auto'
const dialerIcon = 'material-icons text-[#073b4c] text-[40px]! leading-[60px]!'
// Green variant for the primary "Bring in Lead" action.
const dialerGreen = 'w-[60px] h-[60px] rounded-full border-2 border-[#5cb85c] m-auto'
const dialerIconGreen = 'material-icons text-[#5cb85c] text-[40px]! leading-[60px]!'
const caption = 'text-[14px] text-center pt-[10px]'
</script>

<template>
  <div
    class="w-fit mx-auto before:table before:content-[''] after:table after:clear-both after:content-['']"
  >
    <div
      v-if="status == 'ringing'"
      class="w-[70px] float-left mx-[45px] my-0"
      @click="emit('accept')"
    >
      <div
        class="text-center cursor-pointer w-[70px] h-[70px] rounded-full border-2 border-[#5cb85c] m-auto"
      >
        <i class="material-icons text-[#5cb85c] text-[40px]! leading-[65px]!">call</i>
      </div>
      <p :class="caption">
        {{ call?.direction == 'inbound' || call?.type == 'transfer' ? 'Accept' : 'Call' }}
      </p>
    </div>

    <div
      v-if="status == 'ringing'"
      class="w-[70px] float-left mx-[45px] my-0"
      @click="emit('reject')"
    >
      <div
        class="text-center cursor-pointer w-[70px] h-[70px] rounded-full border-2 border-[#eb0000] m-auto"
      >
        <i class="material-icons text-[#eb0000] text-[40px]! leading-[65px]!">call_end</i>
      </div>
      <p :class="caption">Ignore</p>
    </div>

    <div
      v-if="
        status == 'oncall' &&
        call?.lead_id &&
        callUser?.type != 'listen' &&
        callUser?.type != 'whisper' &&
        isTransferStarted &&
        !isOnHold
      "
      :class="ctrl"
      @click="emit('hold')"
    >
      <div :class="dialer">
        <i :class="dialerIcon" class="-ml-[2px]">phone_paused</i>
      </div>
      <p :class="caption">Hold Lead</p>
    </div>

    <div
      v-if="
        status == 'oncall' &&
        call?.lead_id &&
        callUser?.type != 'listen' &&
        callUser?.type != 'whisper' &&
        isTransferStarted &&
        isOnHold
      "
      :class="ctrl"
      @click="emit('unhold')"
    >
      <div :class="dialerGreen">
        <i :class="dialerIconGreen" class="-ml-[2px]">phone_in_talk</i>
      </div>
      <p :class="caption">Bring in Lead</p>
    </div>

    <div
      v-if="
        status == 'oncall' &&
        call?.lead_id &&
        callUser?.type != 'listen' &&
        callUser?.type != 'whisper'
      "
      :class="ctrl"
      @click="emit('transfer')"
    >
      <div :class="dialer">
        <i :class="dialerIcon" class="-ml-[2px]">add_ic_call</i>
      </div>
      <p :class="caption">Transfer</p>
    </div>

    <div
      v-if="status == 'oncall' && callUser?.type != 'listen' && callUser?.type != 'whisper'"
      :class="ctrl"
      @click="emit('dialpad')"
    >
      <div :class="dialer">
        <i :class="dialerIcon">dialpad</i>
      </div>
      <p :class="caption">Keypad</p>
    </div>

    <div v-if="status == 'oncall' && !isMuted" :class="ctrl" @click="emit('mute')">
      <div :class="dialer">
        <i :class="dialerIcon">volume_up</i>
      </div>
      <p :class="caption">Mute</p>
    </div>

    <div
      v-if="status == 'oncall' && isMuted"
      :class="[ctrl, { 'pointer-events-none': callUser?.type == 'listen' }]"
      @click="emit('unmute')"
    >
      <div
        class="w-[60px] h-[60px] rounded-full border-2 m-auto"
        :class="callUser?.type == 'listen' ? 'border-[#d3d3d3]' : 'border-[#073b4c]'"
      >
        <i
          class="material-icons text-[40px]! leading-[60px]!"
          :style="{ color: callUser?.type == 'listen' ? '#d3d3d3' : '#073b4c' }"
          >volume_off</i
        >
      </div>
      <p :class="[caption, { 'text-[#d3d3d3]': callUser?.type == 'listen' }]">Muted</p>
    </div>

    <div v-if="status == 'oncall'" :class="ctrl" @click="emit('hangup')">
      <div class="w-[60px] h-[60px] rounded-full border-2 border-[#eb0000] m-auto">
        <i class="material-icons text-[#eb0000] text-[40px]! leading-[60px]!">call_end</i>
      </div>
      <p :class="caption">Hang Up</p>
    </div>
  </div>
</template>
